import { RouteComponentProps } from 'react-router';
import { withTranslation, WithTranslation } from 'react-i18next';
import {
    BrokerModel,
    brokerModuleEditRoles,
    EditSupplierBrokerRequest,
    SupplierBrokerModel,
    SupplierModel
} from '../../../../api/admin/admin.types';
import React, { FormEvent } from 'react';
import { AppState } from '../../../../store/store.types';
import { ThunkDispatch } from 'redux-thunk';
import { connect } from 'react-redux';
import { SupplierBrokerActionTypes } from '../../types/supplierbroker.types';
import {
    addNewSupplierBroker,
    deleteSupplierBroker,
    editSupplierBroker,
    getSupplierBroker,
    getSupplierBrokerList
} from '../../actions/supplierbroker.actions';
import { FieldControl, FieldGroup, FormBuilder } from 'react-reactive-form';
import { getBrokerList } from '../../actions/broker.actions';
import { SelectedOption, StatusType } from '../../../../api/api.types';
import Breadcrumbs from '../../../../components/breadcrumbs/Breadcrumbs';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import { UserModel } from '../../../../api/authentication/authentication.types';
import SectionTitle from '../../../../components/forms/SectionTitle';
import Select from '../../../../components/forms/Select';
import { BackendIntegrationType } from '../../../../api/core/core.types';
import { TextInput } from '../../../../components/forms/Input';

interface SupplierBrokerRouteProps {
    id: string;
}

interface SupplierBrokerDetailProps extends RouteComponentProps<SupplierBrokerRouteProps>, WithTranslation {
    selectedSupplierBroker: SupplierBrokerModel | null;
    getSelectedSupplierBroker: (id: number) => void;
    editSupplierBroker: (id: number, request: EditSupplierBrokerRequest) => void;
    addNewSupplierBroker: (id: number, request: EditSupplierBrokerRequest) => void;
    getSupplierBrokers: () => Promise<void>;
    deleteSupplierBroker: (id: number) => Promise<void>;
    getBrokerList: () => Promise<void>;
    brokers: BrokerModel[];
    supplierBrokers: SupplierBrokerModel[];
    activeSupplier: SupplierModel | null;
    isNew?: boolean;
    postSuccess: boolean | null;
    lastFetch: number | null;
    user: UserModel | null;
}

interface SupplierBrokerDetailState {
    isModalOpen: boolean;
    isEdit: boolean;
    brokerSelection: { value: string, label: string }[];
}

class SupplierBrokerDetails extends React.Component<SupplierBrokerDetailProps, SupplierBrokerDetailState> {
    readonly state = {
        isModalOpen: false,
        isEdit: false,
        brokerSelection: []
    };

    form = FormBuilder.group({
        companyId: [ '' ],
        agentId: [ '' ],
        sourceCode: [ '' ],
        referralId: [ '' ],
        brokerId: [ '' ],
        supplierReferenceCode: undefined
    });

    componentDidMount() {
        const { id } = this.props.match.params;

        if (id) {
            this.props.getSelectedSupplierBroker(Number(id));
        }

        if (this.props.isNew) {
            this.setState({ isEdit: true });
            this.props.getBrokerList()
              .then(() => {
                  this.props.getSupplierBrokers()
                    .then(() => {
                        const supplierBrokerIds = new Set(this.props.supplierBrokers.map(b => b.id));
                        const brokerSelection: { value: string, label: string }[] =
                          this.props.brokers
                            .filter(broker => !supplierBrokerIds.has(broker.id))
                            .filter(broker => broker.statusType === StatusType.ACTIVE)
                            .map(broker => ({ value: `${broker.id}`, label: broker.name }));
                        this.setState({ brokerSelection: brokerSelection });
                    });
              });
        }

    }

    componentDidUpdate(prevProps: SupplierBrokerDetailProps, prevState: SupplierBrokerDetailState, snapshot?: any) {
        if ((this.props.isNew) && this.props.postSuccess && this.props.selectedSupplierBroker) {
            this.props.history.push(`/admin/brokers/${this.props.selectedSupplierBroker.id}/view`);
        }
        if (this.props.selectedSupplierBroker !== prevProps.selectedSupplierBroker) {
            this.updateFormValue();

        }
    }

    updateFormValue = () => {
        if (!this.props.selectedSupplierBroker) {
            return;
        }
        this.form.patchValue({
            companyId: this.props.selectedSupplierBroker.additionalData.companyId,
            agentId: this.props.selectedSupplierBroker.additionalData.agentId,
            sourceCode: this.props.selectedSupplierBroker.additionalData.sourceCode,
            referralId: this.props.selectedSupplierBroker.additionalData.referralId,
            brokerId: this.props.selectedSupplierBroker.id || undefined,
            supplierReferenceCode: this.props.selectedSupplierBroker.supplierReferenceCode || undefined
        });
    };

    onDeleteConfirm = () => {
        const { id } = this.props.match.params;
        this.props.deleteSupplierBroker(Number(id))
          .then(() => {
              this.props.history.push('/admin/brokers/');
          });
    };

    onSubmit = (e: FormEvent) => {
        e.preventDefault();
        const form = this.form.controls;
        this.form.markAsSubmitted();
        if (this.form.invalid) {
            return;
        }
        const request: EditSupplierBrokerRequest = {
            companyId: form.companyId.value,
            agentId: form.agentId.value,
            sourceCode: form.sourceCode.value,
            referralId: form.referralId.value,
            supplierReferenceCode: form.supplierReferenceCode.value
        };
        if (this.props.selectedSupplierBroker) {
            const { id } = this.props.match.params;
            this.props.editSupplierBroker(Number(id), request);
            this.setState({ isEdit: false });
        } else {
            this.props.addNewSupplierBroker(form.brokerId.value.value, request);
        }
    };

    onCancel = () => {
        if (this.props.isNew) {
            this.props.history.push('/admin/brokers/');
        } else {
            this.updateFormValue();
            this.setState({ isEdit: false });
        }
    };

    onEdit = () => {
        this.setState({ isEdit: true });
    };

    patchField = (option: SelectedOption, field: string) => {
        console.log(option);
        this.form.patchValue({ [ field ]: option });
    };

    getBrokerName = () => {
        const { t } = this.props;
        return (
          <div className="form-group d-flex">
              <div className="col-4 d-flex align-items-center justify-content-end">
                  {this.props.isNew && (
                    <label className="mandatory-field">* </label>
                  )}
                  <label>{t('l.broker.name')}: </label>
              </div>
              <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                  {this.props.brokers && this.props.selectedSupplierBroker && (
                    <div className="form-control-static">
                        {this.props.selectedSupplierBroker.name}
                    </div>
                  )}
                  {this.props.isNew && (
                    <FieldControl
                      name="brokerId"
                      strict={false}
                      render={(props) => (
                        <Select
                          value={this.form.controls.brokerId.value}
                          disableClear
                          className="mr-1"
                          placeholder="Choose broker name"
                          options={this.state.brokerSelection}
                          disableMinWidth
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                          label="broker name"
                          onChange={(value) => this.patchField(value as SelectedOption, 'brokerId')}
                        />
                      )}
                    />
                  )}
              </div>
          </div>
        );
    };

    getBrokerLegalName = () => {
        const { t } = this.props;
        if (!(this.props.isNew && this.state.isEdit)) {
            return (
              <div className="form-group d-flex">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                      <label>{t('l.broker.legal_name')}: </label>
                  </div>
                  <div className="col-2 d-flex align-items-center justify-content-start">
                      {this.props.brokers && this.props.selectedSupplierBroker && (
                        <div className="form-control-static">
                            {this.props.selectedSupplierBroker.legalName}
                        </div>
                      )}
                  </div>
              </div>
            );
        }
    };

    getBrokerComment = () => {
        const { t } = this.props;
        if (!(this.props.isNew && this.state.isEdit)) {
            return (
              <div className="form-group d-flex">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                      <label>{t('l.broker.comment')}: </label>
                  </div>
                  <div className="col-6 d-flex align-items-center justify-content-start">
                      {this.props.brokers && this.props.selectedSupplierBroker && (
                        <div className="form-control-static">
                            {this.props.selectedSupplierBroker.comment}
                        </div>
                      )}
                  </div>
              </div>
            );
        }
    };

    getCompanyId = () => {
        const { t } = this.props;
        if (this.props.activeSupplier?.backendIntegrationType === BackendIntegrationType.BLUEBIRD)
            return (
              <div className="form-group d-flex">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                      <label>{t('l.supplier_broker.company_id')}</label>
                  </div>
                  <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                      {this.props.selectedSupplierBroker && !(this.props.isNew || this.state.isEdit) && (
                        <div className="form-control-static">
                            {this.form.controls.companyId.value}
                        </div>
                      )}
                      {(this.props.isNew || this.state.isEdit) && (
                        <FieldControl
                          name="companyId"
                          render={TextInput}
                        />
                      )}
                  </div>
              </div>
            );
    };

    getSupplierReferenceCode = () => {
        const { t } = this.props;
        return (
          <div className="form-group d-flex">
              <div className="col-4 d-flex align-items-center justify-content-end">
                  <label>Supplier reference code</label>
              </div>
              <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                  {this.props.selectedSupplierBroker && !(this.props.isNew || this.state.isEdit) && (
                    <div className="form-control-static">
                        {this.form.controls.supplierReferenceCode.value}
                    </div>
                  )}
                  {(this.props.isNew || this.state.isEdit) && (
                    <FieldControl
                      name="supplierReferenceCode"
                      render={TextInput}
                    />
                  )}
              </div>
          </div>);
    };

    getReferralId = () => {
        const { t } = this.props;
        if (this.props.activeSupplier?.backendIntegrationType === BackendIntegrationType.BLUEBIRD)
            return (
              <div className="form-group d-flex">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                      <label>{t('l.supplier_broker.referral_id')}: </label>
                  </div>
                  <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                      {this.props.selectedSupplierBroker && !(this.props.isNew || this.state.isEdit) && (
                        <div className="form-control-static">
                            {this.form.controls.referralId.value}
                        </div>
                      )}
                      {(this.props.isNew || this.state.isEdit) && (
                        <FieldControl
                          name="referralId"
                          render={TextInput}
                        />
                      )}
                  </div>
              </div>
            );
    };

    getSourceCode = () => {
        const { t } = this.props;
        if (this.props.activeSupplier?.backendIntegrationType === BackendIntegrationType.BLUEBIRD)
            return (
              <div className="form-group d-flex">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                      <label>{t('l.supplier_broker.source_code')}: </label>
                  </div>
                  <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                      {this.props.selectedSupplierBroker && !(this.props.isNew || this.state.isEdit) && (
                        <div className="form-control-static">
                            {this.form.controls.sourceCode.value}
                        </div>
                      )}
                      {(this.props.isNew || this.state.isEdit) && (
                        <FieldControl
                          name="sourceCode"
                          render={TextInput}
                        />
                      )}
                  </div>
              </div>
            );
    };

    getAgentId = () => {
        const { t } = this.props;
        if (this.props.activeSupplier?.backendIntegrationType === BackendIntegrationType.BLUEBIRD)
            return (
              <div className="form-group d-flex">
                  <div className="col-4 d-flex align-items-center justify-content-end">
                      <label>{t('l.supplier_broker.agent_id')}: </label>
                  </div>
                  <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                      {this.props.selectedSupplierBroker && !(this.props.isNew || this.state.isEdit) && (
                        <div className="form-control-static">
                            {this.form.controls.agentId.value}
                        </div>
                      )}
                      {(this.props.isNew || this.state.isEdit) && (
                        <FieldControl
                          name="agentId"
                          render={TextInput}
                        />
                      )}
                  </div>
              </div>
            );
    };

    getButtons = () => {
        const { t } = this.props;
        if (this.state.isEdit) {
            return (
              <div className="row">
                  <div className="col-12 text-right">
                      <button type="button" className="btn btn-outline-secondary btn-lg mr-2"
                              onClick={this.onCancel}>
                          {t('b.cancel_Generic cancel button translation').toUpperCase()}
                      </button>
                      <button type="submit" className="btn btn-primary btn-lg" onClick={this.onSubmit}>
                          {t('b.save_Generic save button translation').toUpperCase()}
                      </button>
                  </div>
              </div>
            );
        }
    };

    render() {
        if (!this.props.isNew && !this.props.selectedSupplierBroker) {
            return null;
        }

        const showForm = this.state.isEdit || this.props.isNew;

        const { t } = this.props;


        return (
          <main>
              <Breadcrumbs
                items={
                    [
                        { path: '/admin', label: 'Admin' },
                        { path: '/admin/brokers', label: 'Brokers' },
                        {
                            path: null,
                            label: this.props.isNew ? 'New broker' : this.props.selectedSupplierBroker?.name || ''
                        }
                    ]
                }
              />
              <div className="page-content">
                  {this.props.user && userCanAccessComponent(this.props.user, brokerModuleEditRoles) && (
                    <>
                        <div className="row justify-content-end">
                            <div className="col-12 d-flex align-items-center justify-content-between">
                                <h1
                                  className="mb-0">{this.props.isNew || !this.props.selectedSupplierBroker ? 'Add new broker' : this.props.selectedSupplierBroker.name}</h1>
                            </div>
                        </div>
                        <div>
                            <div className="mb-2"/>
                            {this.props.selectedSupplierBroker && !showForm && (
                              <SectionTitle
                                title="DETAILS"
                                showButton={this.props.activeSupplier?.backendIntegrationType === BackendIntegrationType.BLUEBIRD}
                                buttonName={t('b.edit_Generic edit button').toUpperCase()}
                                showDeleteButton
                                deleteButtonName={t('b.delete_Generic delete button').toUpperCase()}
                                onDeleteClick={() => this.setState({ isModalOpen: true })}
                                onEditClick={() => this.onEdit()}
                              />
                            )}
                            {this.props.selectedSupplierBroker && showForm && (
                              <SectionTitle title="DETAILS"
                              />
                            )}
                        </div>
                        <FieldGroup
                          strict={false}
                          control={this.form}
                          render={() => (
                            <form className="form-horizontal" onSubmit={(e) => this.onSubmit(e)}>
                                {this.getBrokerName()}
                                {this.getBrokerLegalName()}
                                {this.getBrokerComment()}
                                {this.getSupplierReferenceCode()}
                                {this.getCompanyId()}
                                {this.getReferralId()}
                                {this.getSourceCode()}
                                {this.getAgentId()}
                                {this.getButtons()}
                            </form>
                          )}
                        />
                    </>)}
              </div>
              <ConfirmationModal
                title="Confirm broker removal"
                content="Are you sure you want to remove this broker?"
                isOpen={this.state.isModalOpen}
                toggle={() => this.setState({ isModalOpen: false })}
                onConfirm={() => this.onDeleteConfirm()}
                onCancel={() => this.setState({ isModalOpen: false })}
              />
          </main>
        );
    }
}

const mapStateToProps = (state: AppState) => ({
    selectedSupplierBroker: state.admin.supplierBrokers.selectedSupplierBroker,
    supplierBrokers: state.admin.supplierBrokers.supplierBrokers,
    brokers: state.admin.brokers.brokers,
    postSuccess: state.admin.supplierBrokers.postSuccess,
    lastFetch: state.admin.supplierBrokers.lastFetch,
    activeSupplier: state.authentication.activeSupplier,
    user: state.authentication.user
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<{}, {}, SupplierBrokerActionTypes>
) => ({
    getSelectedSupplierBroker: (id: number) => dispatch(getSupplierBroker(id)),
    editSupplierBroker: (id: number, request: EditSupplierBrokerRequest) => dispatch(editSupplierBroker(id, request)),
    addNewSupplierBroker: (id: number, request: EditSupplierBrokerRequest) => dispatch(addNewSupplierBroker(id, request)),
    deleteSupplierBroker: (id: number) => dispatch(deleteSupplierBroker(id)),
    getSupplierBrokers: () => dispatch(getSupplierBrokerList()),
    getBrokerList: () => dispatch(getBrokerList())
});

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(SupplierBrokerDetails));